import api from '../services/api';

/**
 * ENViS Live Streams API Service
 * Handles live streaming functionality for ENViS devices
 */
class StreamsApi {
  /**
   * Get live streams from ENViS devices with various filtering modes
   * @param {Object} params - Query parameters
   * @param {string} params.mode - Viewing mode: 'organization', 'network', 'section'
   * @param {number} params.network_id - Filter by network ID (required when mode=network)
   * @param {number} params.section_id - Filter by section ID (required when mode=section)
   * @param {boolean} params.active_only - Filter to active devices only (default: true)
   * @returns {Promise<Object>} Stream data with devices and metadata
   */
  async getStreams(params = {}) {
    try {
      const response = await api.get('/envis-devices/streams', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching streams:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch streams');
    }
  }

  /**
   * Get stream connectivity status for monitoring
   * @param {Object} params - Query parameters
   * @param {string} params.mode - Viewing mode: 'organization', 'network', 'section'
   * @param {number} params.network_id - Filter by network ID
   * @param {number} params.section_id - Filter by section ID
   * @returns {Promise<Object>} Stream status data
   */
  async getStreamStatus(params = {}) {
    try {
      const response = await api.get('/envis-devices/streams/status', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching stream status:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch stream status');
    }
  }

  /**
   * Check if a stream URL is accessible
   * @param {string} streamUrl - The stream URL to check
   * @returns {Promise<boolean>} Whether the stream is accessible
   */
  async checkStreamConnectivity(streamUrl) {
    try {
      // Create a simple connectivity test by trying to load the stream URL
      // This is a basic check - in production you might want to use specific streaming protocols
      const response = await fetch(streamUrl, { 
        method: 'HEAD',
        mode: 'no-cors' // Required for cross-origin requests
      });
      return true; // If no error, stream is likely accessible
    } catch (error) {
      console.warn(`Stream connectivity check failed for ${streamUrl}:`, error);
      return false;
    }
  }

  /**
   * Get available networks for stream filtering
   * @returns {Promise<Array>} Array of networks with streaming devices
   */
  async getNetworksWithStreams() {
    try {
      const response = await api.get('/networks');
      // Filter networks that have devices with streams
      return response.data.filter(network => 
        network.devices && network.devices.some(device => device.stream_url)
      );
    } catch (error) {
      console.error('Error fetching networks with streams:', error);
      throw new Error('Failed to fetch networks');
    }
  }

  /**
   * Get available sections for stream filtering within a network
   * @param {number} networkId - The network ID
   * @returns {Promise<Array>} Array of sections with streaming devices
   */
  async getSectionsWithStreams(networkId) {
    try {
      const response = await api.get(`/networks/${networkId}/sections`);
      // Filter sections that have devices with streams
      return response.data.filter(section => 
        section.devices && section.devices.some(device => device.stream_url)
      );
    } catch (error) {
      console.error('Error fetching sections with streams:', error);
      throw new Error('Failed to fetch sections');
    }
  }

  /**
   * Get a short-lived ticketed URL for a stream
   * @param {number} deviceId - The ID of the device
   * @returns {Promise<string>} The ticketed URL for the stream
   */
  async getStreamTicket(deviceId) {
    try {
      const response = await api.get(`/envis-devices/streams/ticket/${deviceId}`);
      return response.data.ticketedUrl;
    } catch (error) {
      console.error(`Error fetching stream ticket for device ${deviceId}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to get stream ticket');
    }
  }
}

// Create and export a singleton instance
export const streamsApi = new StreamsApi();
export default streamsApi;